import React, { useState, useEffect, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container, Stack, Typography, Breadcrumbs, Box, Paper,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, Chip, CircularProgress, Alert, Collapse,
  IconButton, Divider, Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DownloadRounded,
  ExpandMoreRounded, ExpandLessRounded, CheckCircle,
  Cancel, LinkOff, VerifiedOutlined,
} from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── helpers ────────────────────────────────────────────────────────────────

const HEAT_STATUS_COLOR = {
  quarantine: "warning",
  released:   "success",
  consumed:   "info",
};

const PKG_STATUS_COLOR = {
  draft:       "default",
  active:      "success",
  deactivated: "error",
};

function exportCSV(jobName, trace) {
  const rows = [];
  rows.push([
    "Piece Mark", "Quantity",
    "Heat Number", "Grade", "Shape", "Supplier", "Heat Status", "CMTR Verified", "MTC File",
    "Release Package", "Package Status", "Package Activated",
    "Bill of Lading", "Delivery Date",
  ]);

  for (const item of trace) {
    const heats   = item.heats.length   ? item.heats   : [{}];
    const pkgs    = item.packages.length ? item.packages : [{}];
    const bols    = item.bols.length    ? item.bols    : [{}];

    // Flatten all combinations per piece mark
    for (const h of heats) {
      rows.push([
        item.piece_mark,
        item.quantity,
        h.heat_number    || "",
        h.heat_grade     || "",
        h.heat_shape     || "",
        h.heat_supplier  || "",
        h.heat_status    || "",
        h.cmtr_verified  ? "Yes" : (h.heat_number ? "No" : ""),
        h.mtc_filename   || "",
        pkgs.map((p) => p.package_number || "").filter(Boolean).join(" | ") || "",
        pkgs.map((p) => p.package_status || "").filter(Boolean).join(" | ") || "",
        pkgs.map((p) => p.package_activated_at ? new Date(p.package_activated_at).toLocaleDateString() : "").filter(Boolean).join(" | ") || "",
        bols.map((b) => b.bol_title || "").filter(Boolean).join(" | ") || "",
        bols.map((b) => b.delivery_date ? new Date(b.delivery_date).toLocaleDateString() : "").filter(Boolean).join(" | ") || "",
      ]);
    }
  }

  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `PieceTraceReport_${jobName || "Job"}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ─── sub-component: expanded row detail ──────────────────────────────────────

function TraceDetail({ item }) {
  return (
    <Box sx={{ px: 4, py: 2, bgcolor: "background.default" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>

        {/* Heats */}
        <Box flex={1}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" gutterBottom display="block">
            HEAT(S) ASSIGNED
          </Typography>
          {item.heats.length === 0 ? (
            <Chip icon={<LinkOff />} label="No heat assigned" size="small" color="warning" variant="outlined" />
          ) : item.heats.map((h, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" fontWeight={700}>{h.heat_number}</Typography>
                <Chip label={h.heat_status} size="small" color={HEAT_STATUS_COLOR[h.heat_status] || "default"} />
              </Stack>
              <Typography variant="caption" display="block" color="text.secondary">
                Grade: <strong>{h.heat_grade || "—"}</strong> &nbsp;·&nbsp;
                Shape: <strong>{h.heat_shape || "—"}</strong> &nbsp;·&nbsp;
                Supplier: <strong>{h.heat_supplier || "—"}</strong>
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                {h.cmtr_verified ? (
                  <Chip icon={<VerifiedOutlined />} label="CMTR Verified" size="small" color="success" />
                ) : (
                  <Chip icon={<Cancel />} label="CMTR Not Verified" size="small" color="error" variant="outlined" />
                )}
                {h.mtc_filename && (
                  <Chip label="MTC on file" size="small" color="info" variant="outlined" />
                )}
              </Stack>
            </Paper>
          ))}
        </Box>

        {/* Release Packages */}
        <Box flex={1}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" gutterBottom display="block">
            RELEASE PACKAGE(S)
          </Typography>
          {item.packages.length === 0 ? (
            <Chip icon={<LinkOff />} label="Not in any release package" size="small" color="default" variant="outlined" />
          ) : item.packages.map((p, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" fontWeight={700}>{p.package_number}</Typography>
                <Chip label={p.package_status} size="small" color={PKG_STATUS_COLOR[p.package_status] || "default"} />
              </Stack>
              {p.package_name && (
                <Typography variant="caption" color="text.secondary">{p.package_name}</Typography>
              )}
              {p.package_activated_at && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Activated: {new Date(p.package_activated_at).toLocaleDateString()}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>

        {/* BOL */}
        <Box flex={1}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" gutterBottom display="block">
            BILL OF LADING
          </Typography>
          {item.bols.length === 0 ? (
            <Chip icon={<LinkOff />} label="Not shipped" size="small" color="default" variant="outlined" />
          ) : item.bols.map((b, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={700}>{b.bol_title}</Typography>
              {b.delivery_date && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Delivery: {new Date(b.delivery_date).toLocaleDateString()}
                </Typography>
              )}
              {b.ship_via && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Ship via: {b.ship_via}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>

      </Stack>
    </Box>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function PieceTraceReport() {
  const navigate = useNavigate();
  const [jobs, setJobs]         = useState([]);
  const [jobId, setJobId]       = useState("");
  const [jobName, setJobName]   = useState("");
  const [trace, setTrace]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    ApiCall.get("/job").then((r) => setJobs(r.data?.data?.jobs || [])).catch(() => {});
  }, []);

  const runReport = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setTrace([]);
    setExpanded({});
    try {
      const r = await ApiCall.get("/reports/piece-trace", { params: { jobId: id } });
      setTrace(r.data.data.trace || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleJobChange = (e) => {
    const id = e.target.value;
    setJobId(id);
    const job = jobs.find((j) => j.id === id || j.id === parseInt(id));
    setJobName(job?.name || "");
    runReport(id);
  };

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Summary stats
  const stats = {
    total:       trace.length,
    heatLinked:  trace.filter((i) => i.heats.length > 0).length,
    cmtrOk:      trace.filter((i) => i.heats.some((h) => h.cmtr_verified)).length,
    released:    trace.filter((i) => i.heats.some((h) => h.heat_status === "released" || h.heat_status === "consumed")).length,
    inPackage:   trace.filter((i) => i.packages.length > 0).length,
    shipped:     trace.filter((i) => i.bols.length > 0).length,
  };

  return (
    <>
      <Helmet><title>Piece Trace Report | SFM</title></Helmet>
      <Container maxWidth="xl">

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Breadcrumbs>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HomeRounded color="inherit" fontSize="small" />
              <Typography
                variant="body2" sx={{ cursor: "pointer" }}
                onClick={() => navigate("/reports")}
              >
                Reports
              </Typography>
            </Stack>
            <Typography variant="body2" color="textPrimary">Piece Trace Report</Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined" size="small"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate("/reports")}
            >
              Back
            </Button>
            {trace.length > 0 && (
              <Button
                variant="contained" size="small"
                startIcon={<DownloadRounded />}
                onClick={() => exportCSV(jobName, trace)}
              >
                Export CSV
              </Button>
            )}
          </Stack>
        </Stack>
        <PageGuide
          why="Follow a single piece from material through fabrication to shipping."
          who="QC and the QM, following one piece from material to shipping."
          what="A full traceability report for individual pieces."
          how="Look up a piece to see its heat, status, and history."
          when="When a customer or auditor asks to trace a specific piece."
          standard="AISC §2.5 material traceability"
        />

        <Typography variant="h5" fontWeight={700} mb={0.5}>Piece Trace Report</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Full traceability chain per fabricated item — Heat → CMTR → Release Package → Bill of Lading
        </Typography>

        {/* Job selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 320 }} size="small">
            <InputLabel>Select Job</InputLabel>
            <Select value={jobId} label="Select Job" onChange={handleJobChange}>
              {jobs.map((j) => (
                <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {/* Error */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Stats bar */}
        {!loading && trace.length > 0 && (
          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            {[
              { label: "Total Pieces",    value: stats.total,      color: "default" },
              { label: "Heat Assigned",   value: stats.heatLinked, color: stats.heatLinked === stats.total ? "success" : "warning" },
              { label: "CMTR Verified",   value: stats.cmtrOk,     color: stats.cmtrOk === stats.total ? "success" : "error" },
              { label: "Heat Released",   value: stats.released,   color: stats.released === stats.total ? "success" : "warning" },
              { label: "In Release Pkg",  value: stats.inPackage,  color: stats.inPackage === stats.total ? "success" : "default" },
              { label: "Shipped",         value: stats.shipped,    color: stats.shipped === stats.total ? "success" : "default" },
            ].map((s) => (
              <Chip
                key={s.label}
                label={`${s.label}: ${s.value} / ${stats.total}`}
                color={s.color}
                size="small"
                variant={s.color === "default" ? "outlined" : "filled"}
              />
            ))}
          </Stack>
        )}

        {/* Table */}
        {!loading && trace.length > 0 && (
          <Paper>
            <TableContainer>
              <SmartTable tableId="report-piece-trace" lockedColumns={["Piece Mark", "Heat #", "Heat Status"]} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 700 }}>Piece Mark</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Heat #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CMTR</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Heat Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Release Pkg</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Shipped</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trace.map((item) => {
                    const isOpen = !!expanded[item.item_id];
                    const firstHeat = item.heats[0];
                    const firstPkg  = item.packages[0];
                    const firstBol  = item.bols[0];
                    const cmtrOk    = item.heats.length > 0 && item.heats.every((h) => h.cmtr_verified);
                    const anyMissing = item.heats.length === 0;

                    return (
                      <React.Fragment key={item.item_id}>
                        <TableRow
                          hover
                          sx={{
                            cursor: "pointer",
                            bgcolor: anyMissing ? "rgba(255,152,0,0.04)" : "inherit",
                          }}
                          onClick={() => toggleExpand(item.item_id)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {isOpen ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.piece_mark}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            {firstHeat ? (
                              <Typography variant="body2">{firstHeat.heat_number}</Typography>
                            ) : (
                              <Chip label="No heat" size="small" color="warning" variant="outlined" />
                            )}
                            {item.heats.length > 1 && (
                              <Chip label={`+${item.heats.length - 1}`} size="small" sx={{ ml: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>{firstHeat?.heat_grade || "—"}</TableCell>
                          <TableCell>
                            {item.heats.length === 0 ? (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            ) : cmtrOk ? (
                              <CheckCircle color="success" sx={{ fontSize: 18 }} />
                            ) : (
                              <Cancel color="error" sx={{ fontSize: 18 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {firstHeat ? (
                              <Chip
                                label={firstHeat.heat_status}
                                size="small"
                                color={HEAT_STATUS_COLOR[firstHeat.heat_status] || "default"}
                              />
                            ) : "—"}
                          </TableCell>
                          <TableCell>
                            {firstPkg ? (
                              <Chip
                                label={firstPkg.package_number}
                                size="small"
                                color={PKG_STATUS_COLOR[firstPkg.package_status] || "default"}
                              />
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {firstBol ? (
                              <Chip label={firstBol.bol_title} size="small" color="info" />
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Expanded detail row */}
                        <TableRow>
                          <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isOpen} unmountOnExit>
                              <TraceDetail item={item} />
                              <Divider />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </SmartTable>
            </TableContainer>
          </Paper>
        )}

        {/* Empty state */}
        {!loading && !error && jobId && trace.length === 0 && (
          <Paper sx={{ p: 5, textAlign: "center" }}>
            <Typography color="text.secondary">
              No fabricated items found for this job. Add items via Jobs → Fabricated Items.
            </Typography>
          </Paper>
        )}

        {/* No job selected */}
        {!loading && !jobId && (
          <Paper sx={{ p: 5, textAlign: "center" }}>
            <Typography color="text.secondary">Select a job above to generate the trace report.</Typography>
          </Paper>
        )}

      </Container>
    </>
  );
}
